import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { ColDef, GridReadyEvent } from "ag-grid-community";
import { useOpeningBalance, useOpeningBalanceStore, useCustomer, useBankAccount } from "hooks";
import {
  OpeningBalance,
  OpeningBalanceForm,
  OpeningBalanceQuery,
  OpeningBalanceType,
} from "types";
import SnackbarUtils from "utils/SnackbarUtils";
import { AgGridTable, ConfirmDialog, LoadingState } from "components";
import type { QueryState } from "components/ui-common/AgGridTable/AgGridTable";

const typeOptions: { value: OpeningBalanceType; label: string }[] = [
  { value: "cash_in", label: "Thu (tiền vào)" },
  { value: "cash_out", label: "Chi (tiền ra)" },
  { value: "debt", label: "Công nợ" },
];

const normalizeType = (value: unknown): OpeningBalanceType => {
  if (typeof value === "string") {
    const normalized = value.trim() as OpeningBalanceType;
    if (typeOptions.some((item) => item.value === normalized)) {
      return normalized;
    }
  }
  return typeOptions[0].value;
};

const formatNumber = (value: unknown): string =>
  typeof value === "number" ? value.toLocaleString("vi-VN") : String(value ?? "");

const toNumber = (value: unknown, fallback: number | null = 0): number | null => {
  if (value === null || value === undefined || value === "") return fallback;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const OpeningBalancePage: React.FC = () => {
  const gridApiRef = useRef<GridReadyEvent["api"] | null>(null);
  const addRowRef = useRef<OpeningBalance | null>(null);
  const [addRowVersion, setAddRowVersion] = useState(0);
  const [queryParams, setQueryParams] = useState<OpeningBalanceQuery>({
    page: 1,
    pageSize: 20,
  });
  const [showInlineAddRow, setShowInlineAddRow] = useState<any>('');
  const [confirmRow, setConfirmRow] = useState<OpeningBalance | null>(null);
  const {
    rows,
    setRows,
    upsertRow,
    removeRow,
    isLoading,
    setLoading,
    editingId,
    setEditingId
  } = useOpeningBalanceStore();
  const {
    fetchList,
    createOpeningBalance,
    updateOpeningBalance,
    deleteOpeningBalance,
  } = useOpeningBalance();
  const { fetchList: fetchCustomers } = useCustomer();
  const { fetchList: fetchBankAccounts } = useBankAccount();

  const {
    data: openingBalanceData,
    isFetching,
    refetch,
  } = fetchList(queryParams, true);
  const { data: customerData } = fetchCustomers(
    { page: 1, pageSize: 200 },
    true,
  );
  const { data: bankAccountData } = fetchBankAccounts(
    { page: 1, pageSize: 200 },
    true,
  );

  useEffect(() => {
    if (openingBalanceData?.data) {
      setRows(openingBalanceData.data as OpeningBalance[]);
    }
  }, [openingBalanceData, setRows, isFetching]);

  useEffect(() => {
    setLoading(
      Boolean(
        isFetching ||
        createOpeningBalance.isPending ||
        updateOpeningBalance.isPending ||
        deleteOpeningBalance.isPending,
      ),
    );
  }, [
    isFetching,
    createOpeningBalance.isPending,
    updateOpeningBalance.isPending,
    deleteOpeningBalance.isPending,
    setLoading,
  ]);

  const handleDeleteRow = useCallback((row: OpeningBalance) => {
    setConfirmRow(row);
    setShowInlineAddRow("");
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (!confirmRow) return;
    deleteOpeningBalance.mutate(confirmRow.id, {
      onSuccess: () => {
        removeRow(confirmRow.id);
        setConfirmRow(null);
      },
      onError: () => {
        SnackbarUtils.error("Xóa số dư đầu kỳ không thành công");
      },
    });
  }, [
    confirmRow,
    deleteOpeningBalance,
    removeRow,
  ]);

  const columns = useMemo<ColDef<OpeningBalance>[]>(() => {
    const customerList = (customerData?.data as any[]) ?? [];
    const customerOptions = customerList.map((c) => String(c.id));
    const customerDisplay = new Map(
      customerList.map((c) => [
        String(c.id),
        `${c.code}${c.name ? ` - ${c.name}` : ""}`,
      ]),
    );
    const bankAccounts = (bankAccountData?.data as any[]) ?? [];
    const bankAccountOptions = bankAccounts.map((b) => String(b.id));
    const bankAccountDisplay = new Map(
      bankAccounts.map((b) => [
        String(b.id),
        `${b.bank_name ?? ""}${b.account_number ? ` - ${b.account_number}` : ""}`,
      ]),
    );

    return [
      {
        headerName: "Loại",
        field: "balance_type",
        minWidth: 160,

        filter: "agSetColumnFilter",
        cellEditor: "agSelectCellEditor",
        cellEditorParams: {
          values: typeOptions.map((item) => item.value),
        },
        valueFormatter: (params) =>
          typeOptions.find((item) => item.value === params.value)?.label ||
          params.value,
      },
      {
        headerName: "Số tiền",
        field: "amount",
        minWidth: 140,

        filter: "agNumberColumnFilter",
        cellEditor: "agNumberCellEditor",
        valueFormatter: (params) => formatNumber(params.value),
        valueParser: (params) => toNumber(params.newValue, 0) ?? 0,
      },
      {
        headerName: "Tài khoản ngân hàng",
        field: "bank_account_id",
        minWidth: 180,

        filter: "agNumberColumnFilter",
        cellEditor: "agSelectCellEditor",
        cellEditorParams: { values: bankAccountOptions },
        valueFormatter: (params) =>
          bankAccountDisplay.get(String(params.value)) ?? formatNumber(params.value),
        valueParser: (params) => toNumber(params.newValue, null),
      },
      {
        headerName: "Đối tác",
        field: "partner_id",
        minWidth: 140,

        filter: "agNumberColumnFilter",
        cellEditor: "agSelectCellEditor",
        cellEditorParams: { values: customerOptions },
        valueFormatter: (params) =>
          customerDisplay.get(String(params.value)) ?? formatNumber(params.value),
        valueParser: (params) => toNumber(params.newValue, null),
      },
      {
        headerName: "Ghi chú",
        field: "note",
        flex: 1,
        minWidth: 200,

      },
    ];
  }, [customerData?.data, bankAccountData?.data]);

  const normalizePayload = useCallback(
    (row: Partial<OpeningBalance>): OpeningBalanceForm => {
      const amount = Number(row.amount ?? 0);
      const bankAccountId =
        row.bank_account_id === null || row.bank_account_id === undefined
          ? null
          : Number(row.bank_account_id) || null;
      const partnerId =
        row.partner_id === null || row.partner_id === undefined
          ? null
          : Number(row.partner_id) || null;

      return {
        balance_type: normalizeType(row.balance_type),
        amount: Number.isFinite(amount) ? amount : 0,
        bank_account_id: bankAccountId,
        partner_id: partnerId,
        note: String(row.note ?? "").trim(),
      };
    },
    [],
  );

  type PersistMeta = {
    pinned?: boolean;
    onRevert?: () => void;
    onSuccess?: () => void;
    refetch?: boolean;
  };

  const resetInlineAddRow = useCallback(() => {
    addRowRef.current = {
      balance_type: typeOptions[0].value,
      amount: 0,
      bank_account_id: null,
      partner_id: null,
      note: "",
    } as OpeningBalance;
    setAddRowVersion((v) => v + 1);
  }, []);

  const persistRow = useCallback(
    (row: Partial<OpeningBalance>, meta?: PersistMeta) => {
      const payload = normalizePayload(row);

      if (!payload.balance_type) {
        SnackbarUtils.error("Loại bắt buộc");
        meta?.onRevert?.();
        return;
      }
      if (!Number.isFinite(payload.amount)) {
        SnackbarUtils.error("Số tiền không hợp lệ");
        meta?.onRevert?.();
        return;
      }

      const shouldCreate = meta?.pinned || !row.id;
      if (shouldCreate) {
        createOpeningBalance.mutate(payload, {
          onSuccess: (res) => {
            if (res?.data) {
              upsertRow(res.data as OpeningBalance);
            }
            resetInlineAddRow();
            if (meta?.refetch !== false) {
              refetch();
            }
            meta?.onSuccess?.();
          },
          onError: () => {
            SnackbarUtils.error("Thêm số dư đầu kỳ không thành công");
            meta?.onRevert?.();
          },
        });
        return;
      }

      if (!row.id) {
        SnackbarUtils.error("Không xác định được bản ghi cần cập nhật");
        meta?.onRevert?.();
        return;
      }

      updateOpeningBalance.mutate(
        { id: row.id, ...payload },
        {
          onSuccess: (res) => {
            if (res?.data) {
              upsertRow(res.data as OpeningBalance);
            } else {
              upsertRow({ ...(row as OpeningBalance), ...payload });
            }
            if (meta?.refetch) {
              refetch();
            }
            meta?.onSuccess?.();
          },
          onError: () => {
            SnackbarUtils.error("Cập nhật không thành công");
            meta?.onRevert?.();
          },
        },
      );
    },
    [
      createOpeningBalance,
      normalizePayload,
      refetch,
      resetInlineAddRow,
      updateOpeningBalance,
      upsertRow,
    ],
  );

  const handleSaveRow = useCallback(
    (row: OpeningBalance, meta?: { pinned?: boolean }) => {
      persistRow(row, {
        pinned: meta?.pinned,
        refetch: meta?.pinned || !row.id,
      });
      setShowInlineAddRow("");
    },
    [persistRow],
  );

  const toggleInlineAddRow = useCallback(() => {
    gridApiRef.current?.stopEditing();
    setShowInlineAddRow((prev) => {
      const next = prev ? "" : "add";
      if (next) {
        resetInlineAddRow();
      } else {
        addRowRef.current = null;
        setAddRowVersion((v) => v + 1);
      }
      return next;
    });
  }, [resetInlineAddRow]);

  const handleQueryChange = useCallback(
    (query: QueryState) => {
      setQueryParams((prev) => {
        const next = {
          ...prev,
          page: query.pagination?.page ?? prev.page,
          pageSize: query.pagination?.pageSize ?? prev.pageSize,
          sortModel: query.sortModel,
          filterModel: query.filterModel,
        };
        const unchanged =
          next.page === prev.page &&
          next.pageSize === prev.pageSize &&
          next.sortModel === prev.sortModel &&
          next.filterModel === prev.filterModel;
        return unchanged ? prev : next;
      });
    },
    [setQueryParams],
  );

  const defaultColDef = useMemo<ColDef>(
    () => ({
      editable: (params) =>
        params.data?.id === editingId || params.node.rowPinned === "top",
      sortable: true,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      resizable: true,
      flex: 1,
      minWidth: 140,
    }),
    [editingId],
  );

  const inlineAddRowData = useMemo(
    () =>
      showInlineAddRow === "add" ? (addRowRef.current ?? undefined) : undefined,
    [showInlineAddRow, addRowVersion],
  );

  return (
    <div className="p-6 space-y-6">
      <div className="space-y-1">
        <h1 className="text-2xl font-semibold text-gray-900">Số dư đầu kỳ</h1>
        <p className="text-sm text-gray-600">
          Quản lý số dư đầu kỳ: tạo mới, chỉnh sửa nhanh từng dòng và xoá khi cần.
        </p>
      </div>

      <div className="relative bg-white border border-gray-200 shadow-sm rounded-lg p-4 overflow-hidden">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-base font-semibold text-gray-900">
              Danh sách số dư đầu kỳ
            </h2>
            <p className="text-xs text-gray-500">
              Sử dụng grid để lọc, sắp xếp và chỉnh sửa trực tiếp.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={toggleInlineAddRow}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              {showInlineAddRow ? "Ẩn dòng thêm mới" : "Thêm nhanh trên bảng"}
            </button>
          </div>
        </div>
        <AgGridTable
          rows={rows}
          columns={columns}
          height={520}
          autoHeight={false}
          showAddRow={showInlineAddRow}
          addRowData={inlineAddRowData}
          defaultColDef={defaultColDef}
          onDelete={handleDeleteRow}
          onSave={handleSaveRow}
          onEdit={(params) => {
            setShowInlineAddRow(undefined);
            setEditingId(params.id)
            setTimeout(() => {
              const api = gridApiRef.current;
              if (!api) return;
              api.startEditingCell({
                rowIndex: (params as any)?.node?.rowIndex ?? 0,
                colKey: "balance_type",
              });
            }, 0);
          }}
          getRowId={(params) => String(params.data.id)}
          onQueryChange={handleQueryChange}
          onGridReady={(event) => {
            gridApiRef.current = event.api;
          }}
        />
        {isLoading && (
          <LoadingState
            message="Đang đồng bộ dữ liệu"
            description="Hệ thống đang cập nhật danh sách số dư đầu kỳ, vui lòng chờ giây lát."
          />
        )}
      </div>
      <ConfirmDialog
        open={Boolean(confirmRow)}
        tone="danger"
        title="Xóa số dư đầu kỳ?"
        description={`Bạn chắc chắn muốn xóa số dư "${confirmRow?.id ?? ""}"? Thao tác này không thể khôi phục.`}
        confirmLabel="Xóa"
        cancelLabel="Hủy"
        loading={deleteOpeningBalance.isPending}
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmRow(null)}
      />
    </div>
  );
};

export default OpeningBalancePage;
